import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import moment from "moment";
import { NotificationManager } from "react-notifications";
import { CreacionCierreApi } from "../api/CreacionCierreApi";
import { claveDeFila, esMismaFila } from "../../helpers/filaCierre";
import { getErrorFromResponse } from "../../../../../../../../../utility/utils";

// La fecha manual se guarda para no perderla al ir a la pantalla de pallets de
// una fila y regresar. Va ligada al cierre en proceso y se borra al cerrarlo o
// cancelarlo; en tiempo real no hay nada que guardar
const STORAGE_KEY_FECHA_MANUAL = "prod_bod_cierre_inventario_fecha_manual";

const leerFechaManual = () =>
    JSON.parse(sessionStorage.getItem(STORAGE_KEY_FECHA_MANUAL)) || {};

const borrarFechaManual = () =>
    sessionStorage.removeItem(STORAGE_KEY_FECHA_MANUAL);

// El físico solo es editable en el sobrante, donde el valor que se digita es
// directamente el saldo real del producto
const valoresDeFilas = (filas) =>
    filas.map((fila) => ({
        observaciones: fila.observaciones || "",
        kg_fisico: fila.kg_fisico,
    }));

export const useCierreInventarioForm = ({
    navigateToDetalleCierre,
    navigateToListadoCierres,
}) => {
    const {
        getCierreAbierto,
        cerrarCierre,
        cancelarCierre,
        guardarObservacion,
        guardarReajuste,
        cargando,
    } = CreacionCierreApi();

    const [cierre, setCierre] = useState(null);
    const [filasPacas, setFilasPacas] = useState([]);
    const [filasSobrante, setFilasSobrante] = useState([]);
    const [resumenFisicoPadres, setResumenFisicoPadres] = useState([]);
    const [periodo, setPeriodo] = useState(null);
    const [fechaMinimaCorte, setFechaMinimaCorte] = useState(null);

    // el corte admite reajustes solo si bodega no tuvo movimientos después de
    // él; mientras se consulta una fecha no se sabe todavía
    const [existenciasAlCorte, setExistenciasAlCorte] = useState(null);
    const [fechaUltimoMovimiento, setFechaUltimoMovimiento] = useState(null);

    const fechaManualGuardada = leerFechaManual();

    const {
        control: formControl,
        register: formRegister,
        setValue: formSetValue,
        getValues: formGetValues,
        watch: formWatch,
        trigger: validateForm,
        formState: { errors: formErrors },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            cambiar_fecha: !!fechaManualGuardada.fecha,
            incluir_sin_movimiento: false,
            fecha: fechaManualGuardada.fecha
                ? moment(fechaManualGuardada.fecha)
                : moment(),
            pacas: [],
            sobrante: [],
        },
    });

    const cambiarFecha = formWatch("cambiar_fecha");
    const fecha = formWatch("fecha");

    // los productos sin movimientos ni existencia se ocultan; se piden para
    // reajustar alguno que bodega tiene y el sistema no
    const incluirSinMovimiento = formWatch("incluir_sin_movimiento");

    // En tiempo real el corte lo sella el backend al confirmar, por eso no se
    // manda fecha; solo se envía cuando el encargado decide cerrar un momento
    // ya pasado
    const fechaCorte = (
        cambiarFecha && fecha ? moment(fecha).toISOString() : null
    );

    // evita que una consulta lenta de una fecha anterior pise el resultado
    // de la fecha que el usuario tiene seleccionada
    const consultaRef = useRef(0);

    const limpiar = () => {
        setFilasPacas([]);
        setFilasSobrante([]);
        setResumenFisicoPadres([]);
        setPeriodo(null);
        setExistenciasAlCorte(null);
    };

    const CargarCierreAbierto = async () => {
        if (cambiarFecha && !fechaCorte) return;

        const consulta = consultaRef.current + 1;
        consultaRef.current = consulta;

        // se limpia de inmediato para no mostrar datos de otra fecha
        limpiar();

        try {
            const response = await getCierreAbierto(
                fechaCorte,
                incluirSinMovimiento,
            );

            if (consulta !== consultaRef.current) return;

            setCierre(response.cierre);

            // el documento se abre desde el listado, así que llegar aquí sin
            // uno en proceso solo pasa al entrar por la URL o al recargar
            // después de cancelarlo
            if (!response.cierre) {
                borrarFechaManual();

                NotificationManager.warning(
                    "No hay ningún cierre de inventario en proceso",
                    "Advertencia",
                    3000,
                );

                navigateToListadoCierres();
                return;
            }

            // La fecha guardada es de un cierre que ya se cerró o canceló en
            // otra pantalla: se vuelve al tiempo real, y el cambio de fecha
            // dispara de nuevo la consulta
            const guardada = leerFechaManual();

            if (guardada.cierre_id && guardada.cierre_id !== response.cierre.id) {
                borrarFechaManual();
                formSetValue("cambiar_fecha", false);
                formSetValue("fecha", moment());
                return;
            }

            formSetValue("pacas", valoresDeFilas(response.pacas));
            formSetValue("sobrante", valoresDeFilas(response.sobrante));

            setFilasPacas(response.pacas);
            setFilasSobrante(response.sobrante);
            setResumenFisicoPadres(response.resumen_fisico_por_padre);
            setPeriodo({
                fecha_inicial: response.fecha_inicial,
                fecha_final: response.fecha_final,
            });
            setExistenciasAlCorte(response.existencias_al_corte);
            setFechaUltimoMovimiento(response.fecha_ultimo_movimiento_bodega);

            // no se limpia junto con el período: no depende de la fecha
            // consultada, y dejarlo fijo evita que el datepicker quede sin
            // límite mientras responde el servidor
            setFechaMinimaCorte(
                response.fecha_minima_corte
                    ? moment(response.fecha_minima_corte)
                    : null,
            );
        } catch (error) {
            if (consulta !== consultaRef.current) return;

            const errorMsg = getErrorFromResponse(
                error,
                "Error al calcular el cierre de inventario",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    useEffect(() => {
        CargarCierreAbierto();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fechaCorte, cambiarFecha, incluirSinMovimiento]);

    // hasta conocer el cierre no se toca lo guardado: todavía no se sabe si la
    // fecha es suya
    useEffect(() => {
        if (!cierre) return;

        if (fechaCorte) {
            sessionStorage.setItem(
                STORAGE_KEY_FECHA_MANUAL,
                JSON.stringify({ cierre_id: cierre.id, fecha: fechaCorte }),
            );
        } else {
            borrarFechaManual();
        }
    }, [cierre, fechaCorte]);

    const CancelarCierre = async () => {
        try {
            await cancelarCierre(cierre.id);

            borrarFechaManual();

            NotificationManager.success(
                "Cierre de inventario cancelado",
                "Éxito",
                3000,
            );

            navigateToListadoCierres();
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al cancelar el cierre de inventario",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    // La observación de cada fila se guarda al salir del campo, para que no se
    // pierda al navegar a la pantalla de reajustes
    const GuardarObservacion = async (campo, fila, valor) => {
        const anterior = fila.observaciones || "";
        const nuevo = valor || "";

        if (!cierre || anterior === nuevo) return;

        try {
            await guardarObservacion(cierre.id, {
                ...claveDeFila(campo, fila),
                observaciones: nuevo,
            });

            const actualizar = (filas) =>
                filas.map((item) =>
                    esMismaFila(item, fila)
                        ? { ...item, observaciones: nuevo }
                        : item,
                );

            if (campo === "pacas") {
                setFilasPacas(actualizar);
            } else {
                setFilasSobrante(actualizar);
            }
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al guardar la observación",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    // El sobrante no tiene pallets, así que el físico que se digita en la fila
    // es directamente el saldo real del producto
    const GuardarReajusteSobrante = async (fila, index) => {
        const kgNuevos = formGetValues(`sobrante.${index}.kg_fisico`);

        if (!cierre) return;

        if (kgNuevos === "" || kgNuevos === null || kgNuevos === undefined) {
            return;
        }

        // Se guarda aunque la cantidad no haya cambiado: el backend hace upsert
        // y repetirla es la forma de reconfirmar una línea desactualizada
        // cuando lo contado sigue siendo lo mismo
        try {
            await guardarReajuste({
                ...claveDeFila("sobrante", fila),
                kg_nuevos: kgNuevos,
            });

            // El físico de la fila es justo lo que se acaba de digitar. La
            // línea deja de estar desactualizada porque al guardar el backend
            // relee el saldo del momento, y el sobrante tiene una sola línea
            // por fila, así que ya no queda ninguna por revisar
            setFilasSobrante((filas) =>
                filas.map((item) =>
                    esMismaFila(item, fila)
                        ? {
                              ...item,
                              kg_fisico: Number(kgNuevos),
                              reajustes_obsoletos: 0,
                          }
                        : item,
                ),
            );
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al guardar el reajuste de sobrante",
            );
            NotificationManager.error(errorMsg, "Error", 3000);

            CargarCierreAbierto();
        }
    };

    // Cerrar con líneas desactualizadas aplicaría cantidades que ya no
    // corresponden, así que el backend lo rechaza y aquí se avisa antes
    const contarObsoletos = (filas) =>
        filas.reduce(
            (total, fila) => total + (fila.reajustes_obsoletos || 0),
            0,
        );

    const reajustesObsoletos =
        contarObsoletos(filasPacas) + contarObsoletos(filasSobrante);

    const FormSubmit = async () => {
        const valid = await validateForm();
        if (!valid) return;

        if (reajustesObsoletos > 0) {
            NotificationManager.warning(
                "Hay reajustes desactualizados. Revíselos antes de cerrar.",
                "Advertencia",
                3000,
            );
            return;
        }

        // el datepicker solo existe cuando se cambia la fecha, por eso la
        // exigencia se valida aquí y no como regla del campo
        if (cambiarFecha && !fecha) {
            NotificationManager.warning(
                "Debe indicar la fecha y hora del cierre",
                "Advertencia",
                3000,
            );
            return;
        }

        if (filasPacas.length === 0 && filasSobrante.length === 0) {
            NotificationManager.warning(
                "No hay productos para cerrar en esta fecha",
                "Advertencia",
                3000,
            );
            return;
        }

        const body = {};

        if (fechaCorte) {
            body.fecha_corte = fechaCorte;
        }

        await CerrarCierre(body);
    };

    const CerrarCierre = async (body) => {
        try {
            const cerrado = await cerrarCierre(cierre.id, body);

            borrarFechaManual();

            NotificationManager.success(
                "Cierre de inventario realizado con éxito",
                "Éxito",
                3000,
            );

            navigateToDetalleCierre(cerrado.id);
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al cerrar el inventario",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    return {
        formControl,
        formRegister,
        formErrors,

        cierre,
        cambiarFecha,
        fechaCorte,
        filasPacas,
        filasSobrante,
        resumenFisicoPadres,
        periodo,
        fechaMinimaCorte,
        reajustesObsoletos,
        existenciasAlCorte,
        fechaUltimoMovimiento,

        CancelarCierre,
        GuardarObservacion,
        GuardarReajusteSobrante,
        FormSubmit,
        cargando,
    };
};
